/* eslint-disable no-extra-parens */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { reduxConnect } from '../../../common';
import { translate } from '../../../common/translation/translate';
import { AuthLazyLoad } from '../signin/authLazyLoad';

const defaultZoom = 7;

const mapStoreStateToProps = (storeState) => {
    const { units } = storeState || {};
    const { pressureUnit, windUnit } = units;
    const { enableSourceParameterOnStoriesLinks, pageId, user, isHurricaneSectionEnabled, defaultMapLayerId, enableNewsLandingPageOnWeb2, enableWeatherCamerasPageOnWeb2, enableMapsPageOnWeb2, enableAlertsAllPageOnWeb2, enableLifePollenPageOnWeb2, enableAirQualityLandingPageOnWeb2 } = storeState?.appState?.appState || {};
    const { ActiveLocation } = user || {};
    const { Locality } = ActiveLocation || {};
    const { Latitude: latitude, Longitude: longitude, RouteString: routeString } = Locality || {};

    return {
        pageId,
        latitude,
        longitude,
        routeString,
        isHurricaneSectionEnabled,
        defaultMapLayerId,
        enableSourceParameterOnStoriesLinks,
        windUnit,
        pressureUnit,
        enableNewsLandingPageOnWeb2,
        enableAirQualityLandingPageOnWeb2,
        enableWeatherCamerasPageOnWeb2,
        enableMapsPageOnWeb2,
        enableAlertsAllPageOnWeb2,
        enableLifePollenPageOnWeb2
    };
};

const mapDispatchToProps = {};

//TECHDEBT: Once we remove angular we can use the react-router to handle a lot of this
const navRoutes = [
    {
        textTranslationKey: 'forecasts',
        url: 'weather-forecast/now/',
        activePageIds: ['forecasts/current', 'forecasts/daily', 'forecasts/hourly']
    },
    {
        textTranslationKey: 'alerts',
        url: 'alerts',
        activePageIds: ['alerts', 'alerts/weather', 'alerts/spark', 'alerts/hurricane', 'alerts/fire', 'alerts/pollen', 'alerts/airquality', 'alerts/severestormrisk']
    },
    {
        textTranslationKey: 'life',
        url: 'life/pollen',
        activePageIds: ['life/pollen', 'life/travel', 'life/coldflu', 'life/snow-ski', 'life/snow-ski-detail', 'life/outdoor-sports']
    },
    {
        textTranslationKey: 'newsAndVideo',
        url: 'news/',
        activePageIds: ['news', 'videos']
    },
    {
        textTranslationKey: 'cameras',
        url: 'weather-camera/',
        activePageIds: ['traffic-camera', 'camera-traffic', 'weather-camera']
    },
    {
        textTranslationKey: 'airquality',
        url: 'air-quality/',
        activePageIds: ['air-quality']
    },
    {
        textTranslationKey: 'hurricane',
        url: 'alerts/hurricane/',
        activePageIds: ['alerts/hurricane']
    },
    {
        textTranslationKey: 'hurricane',
        url: 'hurricane/',
        activePageIds: ['hurricane']
    }
];

// eslint-disable-next-line react/display-name
export const NavbarLinkList = reduxConnect( mapStoreStateToProps, mapDispatchToProps )(({ 
    pageId, 
    isHamburgerView, 
    latitude, 
    longitude, 
    routeString,
    isHurricaneSectionEnabled,
    defaultMapLayerId,
    enableSourceParameterOnStoriesLinks,
    windUnit,
    pressureUnit,
    enableNewsLandingPageOnWeb2,
    enableAirQualityLandingPageOnWeb2,
    enableWeatherCamerasPageOnWeb2,
    enableMapsPageOnWeb2,
    enableAlertsAllPageOnWeb2,
    enableLifePollenPageOnWeb2
}) => {

    const pageNavRoutes = useMemo(() => {
        const [firstNavItem, ...restNavItems] = navRoutes;
        return [
            firstNavItem,
            {
                textTranslationKey: 'maps',
                url: 'maps/',
                activePageIds: ['maps'],
                defaultLayerId: defaultMapLayerId
            },
            ...restNavItems
        ];
    }, [defaultMapLayerId]);

    return (
        <NavContainer>
            <NavList isHamburgerView={isHamburgerView}>
                { isHamburgerView
                && <>
                    <NavLi isHamburgerView={isHamburgerView}>
                        <AuthLazyLoad isLargeLink={true} />
                    </NavLi>
                    <NavLi isHamburgerView={isHamburgerView}>
                        <NavLink isHamburgerView={isHamburgerView} href={`${location.origin}/press`} target='_self'>
                            { translate('press') }
                        </NavLink>
                    </NavLi>
                    <NavLi isHamburgerView={isHamburgerView}>
                        <NavLink isHamburgerView={isHamburgerView} href={`${location.origin}/education`} target='_self'>
                            { translate('education') }
                        </NavLink>
                    </NavLi>
                </>
                }
                { /* Need to ensure isHurricaneSectionEnabled is a boolean, this is because of existing issue in AppState 
                    that can return undefined values between renders */ }
                { typeof isHurricaneSectionEnabled === 'boolean' 
                    ? pageNavRoutes.map(route => {
                        let navHref = route.url;
                        
                        // TODO:
                        // Remove lines 148 - 151 when feature is fully deployed to Prod.
                        // Remove Hurricane Nav pointing to alerts/hurricane when new Hurricane section is enabled
                        // and do a similar thing so we don't have another Nav item pointing to the new hurricane (/hurricane) page
                        if ((route.activePageIds[0] === 'alerts/hurricane' && isHurricaneSectionEnabled)
                            || (route.activePageIds[0] === 'hurricane' && !isHurricaneSectionEnabled)) {
                            return null;
                        }

                        if (route.activePageIds[0] === 'maps' && latitude && longitude) {
                            navHref = `${route.url}${routeString}/?layerId=${defaultMapLayerId}&center=${latitude},${longitude},${defaultZoom}`;

                            if (enableMapsPageOnWeb2) {
                                return (
                                    <NavLi isHamburgerView={isHamburgerView} key={route.textTranslationKey}>
                                        <NavLink isHamburgerView={isHamburgerView} href={navHref} aria-label='Live Weather Radar: View an animated map showing past and future precipitation' isActive={route.activePageIds.includes(pageId)} target='_self'>
                                            {translate(route.textTranslationKey)}
                                        </NavLink>
                                    </NavLi>
                                );
                            }
                            return (
                                <NavLi isHamburgerView={isHamburgerView} key={route.textTranslationKey}>
                                    <NavLink isHamburgerView={isHamburgerView} href={navHref} isActive={route.activePageIds.includes(pageId)}>
                                        {translate(route.textTranslationKey)}
                                    </NavLink>
                                </NavLi>
                            );
                        }

                        if (route.activePageIds[0] === 'hurricane' && enableSourceParameterOnStoriesLinks) {
                            navHref = `${location.origin}/hurricane/active?su=${windUnit}&pu=${pressureUnit}`;
                            return (
                                <NavLi isHamburgerView={isHamburgerView} key={route.textTranslationKey}>
                                    <NavLink isHamburgerView={isHamburgerView} href={navHref} target='_self' isActive={route.activePageIds.includes(pageId)}>
                                        {translate(route.textTranslationKey)}
                                    </NavLink>
                                </NavLi>
                            );                
                        }

                        if (route.activePageIds.includes('news') && enableNewsLandingPageOnWeb2) {
                            navHref = `${location.origin}/news`;
                            return (
                                <NavLi isHamburgerView={isHamburgerView} key={route.textTranslationKey}>
                                    <NavLink isHamburgerView={isHamburgerView} href={navHref} target='_self' isActive={route.activePageIds.includes(pageId)}>
                                        {translate(route.textTranslationKey)}
                                    </NavLink>
                                </NavLi>
                            );
                        }

                        if (route.activePageIds.includes('alerts') && enableAlertsAllPageOnWeb2) {
                            navHref = `${location.origin}/alerts`;
                            return (
                                <NavLi isHamburgerView={isHamburgerView} key={route.textTranslationKey}>
                                    <NavLink isHamburgerView={isHamburgerView} href={navHref} target='_self' isActive={route.activePageIds.includes(pageId)}>
                                        {translate(route.textTranslationKey)}
                                    </NavLink>
                                </NavLi>
                            );
                        }

                        if (route.activePageIds.includes('life/pollen') && enableLifePollenPageOnWeb2) {
                            navHref = `${location.origin}/life/pollen`;
                            return (
                                <NavLi isHamburgerView={isHamburgerView} key={route.textTranslationKey}>
                                    <NavLink isHamburgerView={isHamburgerView} href={navHref} target='_self' isActive={route.activePageIds.includes(pageId)}>
                                        {translate(route.textTranslationKey)}
                                    </NavLink>
                                </NavLi>
                            );
                        }
                        
                        if (route.activePageIds.includes('weather-camera') && enableWeatherCamerasPageOnWeb2) {
                            navHref = `${location.origin}/weather-camera`;
                            return (
                                <NavLi isHamburgerView={isHamburgerView} key={route.textTranslationKey}>
                                    <NavLink isHamburgerView={isHamburgerView} href={navHref} target='_self' isActive={route.activePageIds.includes(pageId)}>
                                        {translate(route.textTranslationKey)}
                                    </NavLink>
                                </NavLi>
                            );
                        }

                        if (route.activePageIds.includes('air-quality') && enableAirQualityLandingPageOnWeb2) {
                            navHref = `${location.origin}/air-quality`;
                            return (
                                <NavLi isHamburgerView={isHamburgerView} key={route.textTranslationKey}>
                                    <NavLink isHamburgerView={isHamburgerView} href={navHref} target='_self' isActive={route.activePageIds.includes(pageId)}>
                                        {translate(route.textTranslationKey)}
                                    </NavLink>
                                </NavLi>
                            );
                        }

                        return (
                            <NavLi isHamburgerView={isHamburgerView} key={route.textTranslationKey}>
                                <NavLink isHamburgerView={isHamburgerView} href={navHref} isActive={route.activePageIds.includes(pageId)}>
                                    {translate(route.textTranslationKey)}
                                </NavLink>
                            </NavLi>
                        );
                    })
                    : null
                }
            </NavList>
        </NavContainer>
    );
});

NavbarLinkList.displayName = 'NavbarLinkList';

NavbarLinkList.propTypes = {
    pageId: PropTypes.string,
    isHamburgerView: PropTypes.bool,
    Latitude: PropTypes.number,
    Longitude: PropTypes.number,
    defaultMapLayerId: PropTypes.string
};

const NavContainer = styled.nav`
    width: 100%;
`;

const NavList = styled.ul`
    ${ ({ isHamburgerView }) => isHamburgerView
        ? `
            flex-direction: column;
            width: 100%;
            border-bottom: 1px solid #215870;
        `
        : `
            margin-left: 22px;
        ` }
    padding-left: 0px;
    list-style: none;
    display: flex;
    white-space: nowrap;
`;

const NavLi = styled.li`
    ${ ({ isHamburgerView }) => isHamburgerView ? `
        border-top: 1px solid #215870;
        width: 100%;
        padding-left: 24px;
        padding-top: 6px;
        padding-bottom: 6px;
    `
    : ''}
`;

const NavLink = styled.a`
    ${ ({ isHamburgerView }) => isHamburgerView ? `
        color: #FFFFFF;
        font-size: 28px;
        font-weight: 600;
        text-transform: uppercase;
    `
    : `
        font-size: 16px;
        color: #435560;
        margin-left: 16px;
    ` }
    font-weight: ${ ({ isActive, isHamburgerView }) => isActive && !isHamburgerView ? 700 : 400 };
    text-decoration: none;
`;
